import { useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";

/**
 * FIX: previously this hook called a SECOND payroll mechanism on top of the
 * approval PATCH — createOvertimeAllowance (POST /payrollx/overtime-allowance)
 * after approve, removeOvertimeAllowance (DELETE .../overtime-allowance)
 * after reject. That endpoint pair implemented the OLD per-day, gross,
 * non-netted allowance model that services/overtimeService.js's backend
 * comments explicitly say was removed (it double-counted against the
 * cycle-net overtime row).
 *
 * The backend's setOvertimeApproval already calls
 * recomputeCycleNetOvertimeForAttendance() unconditionally after EVERY
 * approve/reject/manager-only action, inside the same request — it nets
 * overtime against undertime across the whole payroll cycle and is
 * idempotent. That is the single source of truth for the payroll number now.
 *
 * Calling the legacy allowance endpoints on top of that was writing a second,
 * stale, un-netted figure right after the correct cycle-net one had just been
 * computed — which is the "puts the allowance, then removes it later" bug.
 * This hook no longer touches that endpoint at all; it just reports whatever
 * overallStatus the PATCH response comes back with.
 */
const useOvertimeApproval = ({
    BASE_URL,
    language,
    showAlert,
    t,
    onRefresh,
    viewType = "manager",
}) => {
    const isAr = language === "ar";

    const buildHeaders = useCallback(() => ({
        "Content-Type": "application/json",
        "Accept-Language": language,
        "x-view-type": viewType,
    }), [language, viewType]);

    const patchApprovalStatus = useCallback(async (attendanceId, status, rejectionReason = null) => {
        const isReject = status === "rejected";
        const resp = await apiFetch(
            `${BASE_URL}/attendance/overtime/${attendanceId}`,
            {
                method: "PATCH",
                headers: buildHeaders(),
                // The backend requires a non-empty rejectionReason on reject;
                // forward it instead of dropping it (dropping it made every
                // reject fail with REJECTION_REASON_REQUIRED).
                body: JSON.stringify(
                    isReject
                        ? { action: "reject", rejectionReason: rejectionReason || undefined }
                        : { action: "approve" }
                ),
            }
        );
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.error || err.message || "Failed to update approval status");
        }
        return resp.json();
    }, [BASE_URL, buildHeaders]);

    const approveOvertime = useCallback(async (record) => {
        try {
            const result = await patchApprovalStatus(record.id, "approved");
            // The PATCH already triggered recomputeCycleNetOvertimeForAttendance
            // server-side. overallStatus tells us whether this action was the
            // final sign-off (both steps done) or just one of two — no separate
            // payroll call needed either way.
            const overallStatus = result?.data?.overallStatus;

            if (overallStatus === "approved") {
                showAlert?.(
                    t?.approvalSavedTitle || (isAr ? "تم حفظ الموافقة" : "Approval saved"),
                    isAr
                        ? "تم اعتماد طلب الوقت الإضافي بالكامل وإضافته إلى كشف الراتب."
                        : "Overtime request fully approved and added to payroll.",
                    "success"
                );
            } else {
                // Only one of the two approval steps is done so far.
                showAlert?.(
                    t?.approvalSavedTitle || (isAr ? "تم حفظ الموافقة" : "Approval saved"),
                    viewType === "admin"
                        ? (t?.approvalSavedAdmin || (isAr
                            ? "تم تسجيل موافقة الإدارة بنجاح. في انتظار موافقة المدير."
                            : "Admin approval has been recorded. Waiting for manager approval."))
                        : (t?.approvalSavedManager || (isAr
                            ? "تم تسجيل موافقة المدير بنجاح. في انتظار موافقة الإدارة النهائية."
                            : "Manager approval has been recorded. Waiting for final admin approval.")),
                    "success"
                );
            }
            onRefresh?.();
        } catch (err) {
            console.error("approveOvertime error:", err);
            const errorText = err?.error || err?.message || "";
            const managerFirstMessage = /manager approval.*before admin|manager approval.*first/i.test(errorText)
                ? (isAr
                    ? "هذا الطلب يحتاج إلى موافقة المدير أولاً قبل إكمال موافقة الإدارة."
                    : "This overtime request requires manager approval first before the admin approval can be completed.")
                : null;
            showAlert?.(
                t?.approvalErrorTitle || (isAr ? "تعذر إكمال الإجراء" : "Action could not be completed"),
                managerFirstMessage || (t?.approvalErrorMessage || (isAr
                    ? `تعذر اعتماد طلب الوقت الإضافي. ${err.message}`
                    : `We couldn't complete the overtime approval. ${err.message}`)),
                "error"
            );
            throw err; // Re-throw to prevent the caller from thinking the approval succeeded
        }
    }, [patchApprovalStatus, showAlert, onRefresh, isAr, t, viewType]);

    const rejectOvertime = useCallback(async (record, rejectionReason = null) => {
        try {
            // The PATCH already triggered recomputeCycleNetOvertimeForAttendance
            // server-side, which holds this day's overtime out of the cycle net
            // now that it's rejected. No separate allowance-removal call needed.
            await patchApprovalStatus(record.id, "rejected", rejectionReason);

            showAlert?.(
                t?.rejectionSavedTitle || (isAr ? "تم تسجيل الرفض" : "Request rejected"),
                viewType === "admin"
                    ? (t?.rejectionSavedAdmin || (isAr
                        ? "تم رفض الطلب من قبل الإدارة. لن يستمر في المسار التالي."
                        : "The request has been rejected by admin and will not continue further."))
                    : (t?.rejectionSavedManager || (isAr
                        ? "تم تسجيل رفض المدير بنجاح. تم إغلاق الطلب الآن."
                        : "The manager rejection has been recorded. The request is now closed.")),
                "info"
            );
            onRefresh?.();
        } catch (err) {
            console.error("rejectOvertime error:", err);
            showAlert?.(
                t?.rejectionErrorTitle || (isAr ? "تعذر إكمال الرفض" : "Rejection could not be completed"),
                t?.rejectionErrorMessage || (isAr
                    ? `تعذر رفض طلب الوقت الإضافي. ${err.message}`
                    : `We couldn't complete the overtime rejection. ${err.message}`),
                "error"
            );
            throw err; // Re-throw to prevent the caller from thinking the rejection succeeded
        }
    }, [patchApprovalStatus, showAlert, onRefresh, isAr, t, viewType]);

    return { approveOvertime, rejectOvertime };
};

export default useOvertimeApproval;